export const MAX_MESSAGE_LENGTH = 10000;

/**
 * Length of a text in characters as people count them (code points), so an emoji counts once.
 *
 * @param {string} text Any text.
 * @returns {number} Number of code points.
 */
export const countCharacters = (text = '') => Array.from(text || '').length;

/**
 * Cuts a text to a number of code points without splitting a character.
 *
 * @param {string} text Text to cut.
 * @param {number} limit Code points kept; nothing is kept below 1.
 * @returns {string} The kept start of the text.
 */
export const clampToLimit = (text = '', limit = MAX_MESSAGE_LENGTH) =>
    Array.from(text || '')
        .slice(0, Math.max(0, limit))
        .join('');

/**
 * Whether a text holds nothing to send: empty, or only spaces and line breaks.
 *
 * @param {string} text Typed text.
 * @returns {boolean} True when there is nothing to send.
 */
export const isBlankMessage = (text = '') => !(text || '').trim();

/**
 * Part of a paste that still fits in the message (FR103: 10,000 characters, cut silently).
 *
 * @param {string} currentText Text already in the field.
 * @param {string} pastedText Text being pasted.
 * @returns {string} The pasted text, cut to the room left.
 */
export const fitPaste = (currentText, pastedText) =>
    clampToLimit(pastedText, MAX_MESSAGE_LENGTH - countCharacters(currentText));

/**
 * Whether typed input would push the message past the limit.
 *
 * @param {string} currentText Text already in the field.
 * @param {string} insertedText Text the key or input method adds.
 * @param {string} [replacedText] Selected text the input replaces.
 * @returns {boolean} True when the input must be refused.
 */
export const exceedsLimit = (currentText, insertedText, replacedText = '') =>
    countCharacters(currentText) - countCharacters(replacedText) + countCharacters(insertedText) > MAX_MESSAGE_LENGTH;
